import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { cleanText } from '../lib/pi.js';

// ค้นหาสินค้า (PD-first) เพื่อผูกเข้าบรรทัดเอกสาร — ใช้ร่วมกันทั้งใบสั่งซื้อ (PurchaseOrders) และ Import PI
// กติกา: ชื่อ/รูปโชว์ตาม PD (แม่ข้อมูล) เสมอ · แถวตาราง BUY ใช้แค่ id ผูกใบ
// onPick ได้ product ที่มี id ฝั่ง BUY เสมอ — ตัวที่มีเฉพาะใน PD จะถูกสร้างแถวผูก id ให้เองตอนกดเลือก
// รหัสจาก AI/ป้าย (เช่น EBYR810-GREY-333ML) มักยาวกว่า SKU จริง — ค้นเต็มไม่เจอจะลองรหัสต้นให้อัตโนมัติ
export default function ProductPicker({ onPick, onClose, initialQuery = '' }) {
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');

  async function searchOnce(safe) {
    const pat = `%${safe}%`;
    // ค้นสองทาง: ตาราง BUY (เอา id ไว้ผูกใบ) + ชื่อสดจาก PD ผ่าน view ib_pd_products
    const [{ data: local }, { data: pdRows }] = await Promise.all([
      supabase.from('products').select('id, name, sku, image_url')
        .or(`name.ilike.${pat},sku.ilike.${pat}`).order('name').limit(20),
      supabase.from('ib_pd_products').select('sku, name_th, name_en, image_url, default_price')
        .or(`name_th.ilike.${pat},name_en.ilike.${pat},sku.ilike.${pat}`).limit(20),
    ]);
    const pdBySku = new Map((pdRows ?? []).filter(p => p.sku).map(p => [p.sku, p]));
    let rows = local ?? [];
    // ชื่อใหม่ที่เพิ่งแก้ใน PD จะค้นไม่เจอในตาราง BUY — ตามแถว BUY มาด้วย sku ที่ PD เจอ
    const missing = (pdRows ?? []).filter(p => p.sku && !rows.some(r => r.sku === p.sku)).map(p => p.sku);
    if (missing.length) {
      const { data: extra } = await supabase.from('products')
        .select('id, name, sku, image_url').in('sku', missing.slice(0, 20));
      rows = rows.concat(extra ?? []);
    }
    const merged = rows.slice(0, 20).map(r => {
      const p = pdBySku.get(r.sku);
      return p ? { ...r, name: p.name_en || p.name_th || r.name, image_url: p.image_url || r.image_url, default_price: p.default_price ?? null } : r;
    });
    // ตัวที่มีเฉพาะใน PD — เลือกได้เลย เดี๋ยว pick() สร้างแถวผูกฝั่ง BUY ให้
    const haveSku = new Set(rows.map(r => r.sku));
    const pdOnly = (pdRows ?? []).filter(p => p.sku && !haveSku.has(p.sku)).map(p => ({
      id: null, _pdOnly: true, sku: p.sku, name: p.name_en || p.name_th || p.sku,
      image_url: p.image_url ?? null, default_price: p.default_price ?? null,
    }));
    return merged.concat(pdOnly).slice(0, 25);
  }

  useEffect(() => {
    const needle = cleanText(q).trim();
    if (needle.length < 2) { setResults([]); setBusy(false); setNote(''); return; }
    setBusy(true); setNote('');
    const t = setTimeout(async () => {
      const safe = needle.replace(/[,()*%]/g, ' ').trim();
      let rows = await searchOnce(safe);
      // รหัสประกอบยาว (มี "-") หาไม่เจอ → ลองเฉพาะรหัสต้นให้ เช่น EBYR810-GREY-333ML → EBYR810
      if (!rows.length && safe.includes('-')) {
        const base = safe.split('-')[0].trim();
        if (base.length >= 3) {
          rows = await searchOnce(base);
          if (rows.length) setNote(`ไม่พบ “${safe}” — แสดงผลของรหัสต้น “${base}” แทน`);
        }
      }
      setResults(rows);
      setBusy(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // ตัวที่มีเฉพาะใน PD: สร้างแถวผูก id ฝั่ง BUY ก่อน แล้วค่อยส่งกลับ — คนเรียกไม่ต้องรู้เรื่องสองตาราง
  const [linking, setLinking] = useState(false);
  async function pick(p) {
    if (!p._pdOnly) { onPick(p); return; }
    if (linking) return;
    setLinking(true); setErr('');
    try {
      const { data: hit } = await supabase.from('products').select('id, sku').eq('sku', p.sku).maybeSingle();
      if (hit) { onPick({ id: hit.id, sku: hit.sku, name: p.name, image_url: p.image_url }); return; }
      const { data: made, error } = await supabase.from('products')
        .insert({ sku: p.sku, name: p.name, image_url: p.image_url ?? null })
        .select('id, sku, name, image_url').single();
      if (error) throw error;
      onPick(made);
    } catch (e) {
      setErr(`ผูกสินค้าจาก PD ไม่สำเร็จ: ${e.message ?? e}`);
    } finally {
      setLinking(false);
    }
  }

  return (
    <>
      <div className="scrim open" style={{ zIndex: 60 }} onClick={onClose} />
      <div className="prod-picker">
        <div className="pp-head">
          <input autoFocus className="under" placeholder="ค้นหาสินค้า — ชื่อ หรือ SKU" value={q} onChange={e => setQ(e.target.value)} />
          <button className="xbtn" onClick={onClose} aria-label="ปิด">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
          </button>
        </div>
        <div className="pp-list">
          {busy && <p className="sd-empty">กำลังค้นหา…</p>}
          {note && !busy && <p className="sd-empty">{note}</p>}
          {err && <p className="err-note" style={{ padding: '0 4px' }}>{err}</p>}
          {!busy && q.trim().length < 2 && <p className="sd-empty">พิมพ์อย่างน้อย 2 ตัวอักษร (ชื่อสินค้า หรือ SKU)</p>}
          {!busy && q.trim().length >= 2 && !results.length && <p className="sd-empty">ไม่พบสินค้า “{q}”</p>}
          {results.map(p => (
            <button className="pp-item" key={p.id ?? `pd-${p.sku}`} onClick={() => pick(p)} disabled={linking}>
              {p.image_url ? <img src={p.image_url} alt="" /> : <div className="pp-noimg" />}
              <div>
                <div className="pp-name">{p.name}</div>
                <div className="pp-sku mono">{p.sku ?? '—'}{p.default_price != null ? ` · ขาย ฿${Number(p.default_price).toLocaleString()}` : ''}{p._pdOnly ? ' · จาก PD' : ''}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
